import { useAuth0 } from "@auth0/auth0-react";
import React from "react";

import { Button } from "@fronterahealth/frontera-ui-components";

import fronteraIcon from "../../assets/icon.png";

interface OnboardingShellProps {
  children: React.ReactNode;
  onContinue?: () => void;
  onBack?: () => void;
  // Low-emphasis escape (e.g. "Skip for now"), rendered far-left of the footer.
  onSkip?: () => void;
  continueLabel?: string;
  backLabel?: string;
  skipLabel?: string;
  continueDisabled?: boolean;
  // Disable the Back / Skip escapes too (e.g. while a Finish action is mid-flight,
  // so the user can't navigate away while requests are still completing).
  backDisabled?: boolean;
  skipDisabled?: boolean;
  // Progress stepper: 1-based position and total step count. When both are set,
  // a subtle "Step X of Y" label + rio progress bar renders at the top of the
  // column. Omitted on the loader (no shell) and any single-screen step.
  currentStep?: number;
  totalSteps?: number;
}

// Shared chrome for every onboarding step: full-bleed white page, Frontera logo
// at the top of a centered ~748px column, content, then a right-aligned footer
// button row. Matches the Figma onboarding frames (logo top-left of column,
// Continue bottom-right). The loader step renders on its own, without this shell.
export const OnboardingShell: React.FC<OnboardingShellProps> = ({
  children,
  onContinue,
  onBack,
  onSkip,
  continueLabel = "Continue",
  backLabel = "Back",
  skipLabel = "Skip for now",
  continueDisabled = false,
  backDisabled = false,
  skipDisabled = false,
  currentStep,
  totalSteps,
}) => {
  const { logout } = useAuth0();

  return (
    <div className="min-h-screen w-full bg-limestone-100 p-8">
      <div className="relative flex min-h-[calc(100vh-64px)] w-full justify-center rounded-2xl border border-border-subtle bg-white px-6">
        {/* Always-available escape so a user can leave a half-finished onboarding
            and resume it on next sign-in (progress is persisted server-side as
            each step saves; the redirect gate re-routes incomplete users here). */}
        <button
          type="button"
          onClick={() =>
            logout({
              logoutParams: { returnTo: `${globalThis.location.protocol}//${globalThis.location.host}` },
            })
          }
          className="absolute right-6 top-6 text-sm text-text-tertiary underline-offset-2 transition-colors hover:text-text-secondary hover:underline"
        >
          Logout
        </button>
        <div className="animate-onboarding-step-in flex w-full max-w-[680px] flex-col pb-12 pt-[120px]">
          <img src={fronteraIcon} alt="Frontera" className="h-8 w-8" />
          {typeof currentStep === "number" && typeof totalSteps === "number" && totalSteps > 0 && (
            <div className="mt-8">
              <p className="text-xs font-medium text-text-tertiary">
                Step {currentStep} of {totalSteps}
              </p>
              <div className="mt-2 h-1 w-32 overflow-hidden rounded-full bg-limestone-200">
                <div
                  className="h-full rounded-full bg-rio-600 transition-[width] duration-300 ease-out"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                />
              </div>
            </div>
          )}
          <div className="mt-8 flex-1">{children}</div>
          <div className="mt-10 flex items-center justify-between gap-3">
            <div>
              {onSkip && (
                <Button
                  text={skipLabel}
                  variant="secondary"
                  buttonStyle="ghost"
                  onClick={onSkip}
                  disabled={skipDisabled}
                />
              )}
            </div>
            <div className="flex items-center gap-3">
              {onBack && (
                <Button
                  text={backLabel}
                  variant="secondary"
                  buttonStyle="border"
                  onClick={onBack}
                  disabled={backDisabled}
                />
              )}
              {onContinue && (
                <Button
                  text={continueLabel}
                  variant="primary"
                  buttonStyle="filled"
                  onClick={onContinue}
                  disabled={continueDisabled}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
